import { getStatusType } from "#common/Table/rendererHelper";
import { useNavigate } from "#routers/hooks";
import { linkToFormSubmissionDetail } from "#routers/links";
import { BulkUpdateProgressBanner } from "#src/batteries-included-components/Banners/BulkUpdateProgressBanner/BulkUpdateProgressBanner";
import { BulkDeleteFormSubmissionDialog } from "#src/batteries-included-components/Dialogs/BulkDeleteFormSubmissionsDialog";
import {
  FormSubmissionsTableFilterArea,
  FormSubmissionsTableFilterAreaDrawerContent,
  FormSubmissionsViewFilterArea,
} from "#src/batteries-included-components/FilterAreas/FormsFilterAreas";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable/IsFeatureAvailable";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { useExportFormSubmissionAsPDF } from "#src/components/Forms/useExportFormSubmissionAsPDF";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#src/components/Redux/reducers/tableStateReducer";
import {
  useBulkDeleteFormSubmissions,
  useListFormSubmissions,
} from "#src/components/hooks/adapters/useFormSubmissions";
import { useGetManyUsers } from "#src/components/hooks/adapters/useUsers";
import { useHasPermission } from "#src/contexts/AuthenticatedContext.helpers";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { linkToFormCategoryDetails } from "#src/routes/forms/categories/[categoryId]";
import { linkToFormTemplateDetail } from "#src/routes/forms/categories/[categoryId]/templates/[formTemplateId]";
import { linkToUpdateFormSubmission } from "#src/routes/forms/categories/[categoryId]/templates/[formTemplateId]/update-form-submission";
import {
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableUserInterfaceConfiguration,
} from "#src/utils/table";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  DataTablePanel,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  Icon,
  ItemAction,
  Pill,
} from "@validereinc/common-components";
import {
  FormSubmissionAdapter,
  FormSubmissionGetListRequestType,
  FormSubmissionStatus,
  FormSubmissionType,
  ResourceDefinitions,
  Resources,
  SortDirection,
  UserReturnType,
  type GroupedByEntryType,
} from "@validereinc/domain";
import { toFlattenedObject, toTitleCaseString } from "@validereinc/utilities";
import React, { useMemo, useState } from "react";

export const NonDraftFormSubmissionsList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [
    tableConfigStorageKey,
    filterConfigStorageKey,
    viewConfigStorageKey,
    tableStateStorageKey,
    groupByTablesStateStorageKey,
  ] = useStorageKey(
    [
      "table-configuration",
      "filter-configuration",
      "view-configuration",
      "table-state",
      "group-by-tables-state",
    ],
    ["forms", "submissions", "list"]
  );
  const [bulkDeleteFormSubmissionsJobStorageKey] = useStorageKey(
    ["async-job"],
    "form-submissions-bulk-delete"
  );
  const [canEditFormSubmissions] = useHasPermission("form_submissions:edit");
  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
    matchingCompanyConfiguration,
    matchingPersonalConfiguration,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);
  const [viewFilters] = useStickyState<FormSubmissionGetListRequestType>(
    {},
    viewConfigStorageKey
  );
  const [tableFilters] = useStickyState<FormSubmissionGetListRequestType>(
    {},
    filterConfigStorageKey
  );
  const filters = useMemo(
    () => ({ ...tableFilters, ...viewFilters }),
    [tableFilters, viewFilters]
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const [selectedFormSubmissions, setSelectedFormSubmissions] = useState<
    Record<string, FormSubmissionType>
  >({});
  const [isSelectionInverted, setIsSelectionInverted] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // @ts-expect-error TODO: fix filter typing
  const { created_at, status, ...restFilters } = filters;
  const queryPayload: Parameters<typeof FormSubmissionAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters: {
      created_at,
      ...toFlattenedObject({
        status: !status
          ? Object.values(FormSubmissionStatus).filter(
              (s) => s !== FormSubmissionStatus.DRAFT
            )
          : status,
        "form_schema.status": "active",
        ...restFilters,
      }),
    },
    meta: { answers: true },
  };
  const {
    data: submissionsResponse,
    isLoading: isSubmissionsLoading,
    isFetching: isSubmissionsFetching,
  } = useListFormSubmissions(queryPayload);

  const items = submissionsResponse?.data ?? [];
  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...queryPayload,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map((key) => groupByTablesStates.get(key) ?? queryPayload)
  );

  const formSubmissionsGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: [Resources.FORM_SUBMISSION, "getList", d.payload],
      queryFn: () => FormSubmissionAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const createdByUsersQueries = useGetManyUsers(
    Array.from(
      new Set(
        tableState.groupBy.length
          ? formSubmissionsGroupedByQueries.flatMap(
              (q) =>
                q.data?.data.map(({ created_by }) => created_by ?? "") ?? []
            )
          : submissionsResponse?.data.map(({ created_by }) => created_by ?? "")
      )
    )
  );
  const usersMap = useMemo(
    () =>
      createdByUsersQueries?.reduce<Record<string, UserReturnType>>(
        (map, query) => {
          if (!query.data) {
            return map;
          }

          map[query.data.id] = query.data;
          return map;
        },
        {}
      ) ?? {},
    [createdByUsersQueries]
  );

  const exportPDFMutation = useExportFormSubmissionAsPDF({
    includeEmptyAnswers: false,
    metaUserDataMap: usersMap,
  });

  const { handleBulkDelete } = useBulkDeleteFormSubmissions({
    jobKey: bulkDeleteFormSubmissionsJobStorageKey,
    formSubmissionGetListParams: queryPayload,
    selectedFormSubmissions,
    isSelectionInverted,
    setIsDeleting,
  });

  const isLoading =
    isSubmissionsLoading ||
    createdByUsersQueries.some((q) => q.isLoading) ||
    isDeleting;

  const headers: Array<HeaderType<FormSubmissionType>> = useMemo(
    () => [
      {
        label: "Submission Name",
        key: "id",
        renderComponent: ({ item }) => (
          <RoutingLink to={linkToFormSubmissionDetail(item?.id)}>
            {`${item?.form_schema?.name} - ${item?.id?.slice(0, 7)}`}
          </RoutingLink>
        ),
      },
      {
        label: "Status",
        key: "status",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <Pill
            variant={getStatusType(item.status, "form").type}
            isLoading={isLoading}
          >
            {toTitleCaseString(item.status, {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        ),
      },
      {
        label: "Form Template",
        key: "form_schema.id",
        isGroupable: true,
        renderComponent: ({ item }: { item: FormSubmissionType }) => {
          return (
            <RoutingLink
              to={linkToFormTemplateDetail(
                item.form_schema?.form_category_id,
                item.form_schema_id
              )}
            >
              {item?.form_schema?.name}
            </RoutingLink>
          );
        },
      },
      {
        label: "Form Category",
        key: "form_schema.form_category_id",
        renderComponent: ({ item }) => {
          return (
            <RoutingLink
              to={linkToFormCategoryDetails(
                item?.form_schema?.form_category_id
              )}
            >
              {item?.form_schema?.form_category?.name}
            </RoutingLink>
          );
        },
      },
      {
        label: "Created At",
        key: "created_at",
        isSortable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item.created_at}
            withTime
          />
        ),
      },
      {
        label: "Last Saved At",
        key: "updated_at",
        isSortable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item.updated_at}
            withTime
          />
        ),
      },
      {
        label: "Submitted By",
        key: "created_by",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) =>
          item.created_by ? (
            <UserDataDisplay
              id={item.created_by}
              name={usersMap[item.created_by]?.name ?? "n/a"}
              isLoading={isLoading}
            />
          ) : (
            "-"
          ),
      },
    ],
    [isLoading, usersMap]
  );

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: formSubmissionsGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemId: (item) => item.id,
    totalSelectableItems,
  });

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <FormSubmissionsViewFilterArea
          viewConfigStorageKey={viewConfigStorageKey}
        />
      </div>
      <BulkUpdateProgressBanner
        jobStorageKey={bulkDeleteFormSubmissionsJobStorageKey}
        setSelectedItems={setSelectedFormSubmissions}
        setIsRunning={setIsDeleting}
        setIsSelectionInverted={setIsSelectionInverted}
        invalidate={() => {
          queryClient.invalidateQueries({
            queryKey: [Resources.FORM_SUBMISSION],
          });
        }}
        subject={{
          singular: ResourceDefinitions.form_submission.label.singular,
          plural: ResourceDefinitions.form_submission.label.plural,
        }}
        action={"delete"}
      />
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        filterComponent={
          <FormSubmissionsTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
            filterDrawerContentSlot={
              <FormSubmissionsTableFilterAreaDrawerContent
                hasStatusFilter
                hasCategoryFilter
                hasTemplateFilter
                hasDatasetSubmissionIdFilter
                hasSubmittedByFilter
              />
            }
          />
        }
        panelProps={{
          title: "Submissions",
        }}
        dataTableProps={{
          intent: "simplicity-first",
          items: submissionsResponse?.data ?? [],
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: submissionsResponse?.total_entries,
            pageSizeText: "rows per page",
          },
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          headers,
          isLoading: isLoading,
          isBusy: isSubmissionsFetching,
          selected: selectedFormSubmissions,
          isSelectionInverted: isSelectionInverted,
          onSelectionChange: (value, shouldInvertSelection) => {
            setSelectedFormSubmissions(value);
            setIsSelectionInverted(shouldInvertSelection ?? false);
          },
          onPaginationChange: updateTableState,
          onSortChange: updateTableState,
          getItemId: (item) => item.id,
          getItemBulkActions: ({ buttonProps }) => [
            <IsFeatureAvailable
              key="delete"
              featureFlagQuery="core:forms"
              permissionQuery="form_submissions:delete"
              fallbackChildren={null}
            >
              <Button
                {...buttonProps}
                key="delete"
                variant="error-outline"
                slotLeft={<Icon variant="trash" />}
                isLoading={isDeleting}
                onClick={() => setShowDeleteDialog(true)}
              >
                Delete
              </Button>
            </IsFeatureAvailable>,
          ],
          emptyStateProps: {
            title: "There are no submissions to display",
          },
          getItemActions: ({
            item,
          }: {
            item: FormSubmissionType;
          }): ItemAction[] => [
            ...(item?.status !== FormSubmissionStatus.DRAFT &&
            item?.form_schema?.form_submission_editable &&
            canEditFormSubmissions
              ? ([
                  {
                    label: "Edit",
                    buttonProps: {
                      icon: "pencil-simple",
                      onClick: () =>
                        navigate({
                          pathname: linkToUpdateFormSubmission(
                            item?.form_schema?.form_category_id,
                            item?.form_schema?.id,
                            item?.id
                          ),
                        }),
                    },
                  },
                ] satisfies ItemAction[])
              : []),
            {
              label: "Export as PDF",
              buttonProps: {
                icon: "share",
                isLoading: exportPDFMutation.isLoading,
                onClick: () => {
                  exportPDFMutation.mutate(item);
                },
              },
            },
          ],
        }}
        {...groupByDataTableProps}
        columnConfigurationProps={{
          onSaveColumnConfigurationAsDefault: (colsToSave) =>
            saveDefaultConfigurationMutation
              .mutateAsync({
                name: "Default for non-draft Form Submissions list table",
                configuration: {
                  columns: colsToSave,
                },
              })
              .then(() => true)
              .catch(() => false),
          isLoadingSaveDefaultColumnConfiguration:
            saveDefaultConfigurationMutation.isLoading,
          isSaveDefaultColumnConfigurationAvailable:
            isSaveConfigurationAvailable,
          onResetColumnConfiguration: () =>
            resetConfigurationMutation.mutateAsync(),
          onDeleteDefaultColumnConfiguration: () =>
            deleteDefaultConfigurationMutation
              .mutateAsync()
              .then(() => true)
              .catch(() => false),
          isLoadingDeleteDefaultColumnConfiguration:
            deleteDefaultConfigurationMutation.isLoading,
          isDeleteDefaultColumnConfigurationAvailable:
            isDeleteConfigurationAvailable && !!matchingCompanyConfiguration,
          isLoadingDefaultColumnConfiguration:
            isLoadingTableUserInterfaceConfiguration,
          isDefaultColumnConfigurationAvailable: !!matchingDefaultConfiguration,
          isCompanyColumnConfigurationAvailable: !!matchingCompanyConfiguration,
          defaultColumnConfiguration:
            matchingDefaultConfiguration?.configuration?.columns,
          companyDefaultColumnConfiguration:
            matchingCompanyConfiguration?.configuration?.columns,
          personalDefaultColumnConfiguration:
            matchingPersonalConfiguration?.configuration?.columns,
        }}
      />
      <BulkDeleteFormSubmissionDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
        }}
        handleBulkDelete={handleBulkDelete}
        formSubmissionsCount={
          isSelectionInverted
            ? (submissionsResponse?.total_entries ?? 0)
            : Object.keys(selectedFormSubmissions).length
        }
      />
    </>
  );
};
